import * as React from "react";

import "./mobile-cart-card.scss";

import {IconProp}        from "@fortawesome/fontawesome-svg-core";
import {
    faBan,
    faCamera,
    faCartArrowDown,
    faDownload,
    faEllipsis,
    faFileArchive,
    faFilePdf,
    faMagnifyingGlass,
    faShareNodes,
    faTruckFast,
}                        from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import {AnyAction}            from "@Common/react/any-action";
import {ComponentLink,
    ComponentNext, makeChain} from "@Common/react/cst2";
import {UseState}             from "@Common/react/use-state";
import {Types}                from "@Common/types";
import {formatDate1}          from "@Common/ui-components/format-date";
import {formatMoney}          from "@Common/ui-components/language/format-money";
import {RangeLoader}          from "@Common/react/range-loader";

import {mapMouseEventAsPosition}                                                   from "../../common/mobile-card/dropdown/dropdown";
import {CartAttribute, CartAttributeDict, CartAttributeKey}                        from "./cart-attributes";
import {ItemAttribute, ItemAttributeDict, ItemAttributeKey, ItemAttributesService} from "./item-attributes";

type Cart = Types.Apis.Payloads.Next.Item.GetHistory.Cart;
type Item = Types.Apis.Payloads.Next.Item.GetHistory.Item;

type ItemAction = ItemAttribute & {run: () => Promise<void>};

const CART_DETAILS_ORDERED_KEYS = [
    "date",
    "branch",
    "agent",
] as CartAttributeKey[];

const ITEM_ACTIONS_ICONS_DICT = {
    cancel              : faBan,
    download            : faDownload,
    downloadImagePDF    : faFilePdf,
    downloadPhotos      : faCamera,
    downloadProof       : faFilePdf,
    downloadZip         : faFileArchive,
    reorder             : faCartArrowDown,
    reorderDisallowed   : faBan,
    showDetails         : faMagnifyingGlass,
    trackDeliveryStatus : faTruckFast,
    xpressLinks         : faShareNodes,
} as {[key in ItemAttributeKey]?: IconProp};

const ITEM_ACTIONS_ORDERED_KEYS = [
    "download",
    "downloadImagePDF",
    "downloadProof",
    "downloadZip",
    "downloadPhotos",
    "trackDeliveryStatus",
    "xpressLinks",
    "showDetails",
    "reorder",
    "reorderDisallowed",
    "cancel",
] as Array<keyof ItemAttributeDict & {[key in ItemAttributeKey]: ItemAction}>;

const ITEM_DETAILS_ORDERED_KEYS = [
    "price",
    "programName",
    "trackDeliveryStatus",
] as ItemAttributeKey[];

export const MobileCartCard = ({attributes, cart, cartObject}:{
    attributes  : CartAttributeDict,
    cart        : Cart,
    cartObject  : ComponentNext<typeof RangeLoader<Types.Apis.Payloads.Next.Item.GetHistory.Response, Cart>>
}): JSX.Element => (
    makeChain<JSX.Element>()
    .then("itemAttributesService", ({}, next:ComponentLink<typeof ItemAttributesService, "renderChildren">) => ItemAttributesService({cart, renderChildren: next, onItemCancelled: cartObject.reload}))
    .then(({itemAttributesService: {mapItemAsAttributes}}) => (
        <article className="mobile-cart-card mobile-cart-card-3497">
            <header>
                <div className="titles">
                    <span className="title" xd-qa-type="label" xd-qa-name="Cart ID">Cart ID. {cart.cartId}</span>
                </div>
                {attributes.printReceipt && <a onClick={attributes.printReceipt.run}>Receipt</a>}
                <aside>
                    <ul className="details">
                        {attributes.itemsCount && (
                            <li>
                                <span className="label">{attributes.itemsCount.label}</span>
                                <span xd-qa-type="label" xd-qa-name="Orders">{attributes.itemsCount.value}</span>
                            </li>
                        )}
                        {attributes.priceTotal && (
                            <li className="strong">
                                <span xd-qa-type="label" xd-qa-name="Total">{formatMoney(attributes.priceTotal.value, {useFreeLabel:false, alwaysShowDollarSymbol:true})}</span>
                            </li>
                        )}
                    </ul>
                </aside>
            </header>
            <main>
                <ul className="details">
                    {CART_DETAILS_ORDERED_KEYS.filter(key => key in attributes).map(key => <CartCardDetail key={key} attribute={attributes[key]}/>)}
                </ul>
                <section className="items">
                    {cart.items.map(item => <ItemCard key={item.itemId} attributes={mapItemAsAttributes(item)} item={item}/>)}
                </section>
            </main>
        </article>
    ))
    .run()
);

const ItemCard = ({attributes, item}:{
    attributes  : ItemAttributeDict,
    item        : Item,
}) => (
    makeChain<JSX.Element>()
    .then("menuState", ({}, next:ComponentLink<typeof UseState<{right: number, top: number}>>) => UseState({next, initValue: null}))
    .then(({menuState: {onChange: setMenuPosition, value: menuPosition}}) => (
        <article className={item.transOrderStatus} xd-qa-type="card" xd-qa-name={`Order ${item.itemId}`}>
            <header>
                <div className="titles">
                    <span className="title"><a onClick={attributes.showDetails?.run}>{attributes.name.value ?? "?"}</a></span>
                    <span className="subtitle">Order ID <span xd-qa-type="label" xd-qa-name="Order ID">{item.itemId}</span></span>
                </div>
                <aside>
                    <ul className="details">
                        <li>
                            <span className="label">Qty</span>
                            <span xd-qa-type="label" xd-qa-name="Qty">{item.selections.quantity}</span>
                        </li>
                        <li xd-qa-type="button" xd-qa-name="Actions">
                            <button
                                onClick={(e) => {
                                    const position = mapMouseEventAsPosition(e);
                                    window.setTimeout(() => setMenuPosition(position), 0);
                                }}
                                className="icon"
                            ><FontAwesomeIcon icon={faEllipsis}/></button>
                        </li>
                    </ul>
                </aside>
            </header>
            <main>
                <ul className="details">
                    {ITEM_DETAILS_ORDERED_KEYS.filter(key => key in attributes).map(
                        key => <ItemCardDetail key={key} attribute={attributes[key]} item={item}/>
                    )}
                </ul>
                {attributes.thumbnail && <aside>
                    <img onClick={attributes.preview?.run} src={item.thumbnail} alt={attributes.name.value}/>
                </aside>}
            </main>
            {menuPosition && (
                <ItemCardMenu
                    actions={ITEM_ACTIONS_ORDERED_KEYS.filter(key => key in attributes && attributes[key].run).map(key => attributes[key])}
                    position={menuPosition}
                    onClose={() => setMenuPosition(null)}
                />
            )}
        </article>
    ))
    .run()
);

const CartCardDetail = ({attribute}:{
    attribute   : CartAttribute,
}) => {
    if ("value" in attribute) {
        return (
            <li>
                <span className="label">{attribute.label}</span>
                <span xd-qa-type="label" xd-qa-name={attribute.label}>{attribute.value instanceof Date ? formatDate1(attribute.value) : attribute.value}</span>
            </li>
        );
    } else {
        throw new Error(`Missing renderer for cart attribute '${attribute.key}'`);
    }
};

const ItemCardMenu = ({actions, onClose: emitClose, position}:{
    actions  : Array<ItemAction>,
    onClose  : () => void,
    position : {right: number, top: number},
}) => (
    <div className="mobile-dropdown" style={position}>
        <AnyAction action={emitClose}/>
        {actions.map((action) => <React.Fragment key={action.key}>{ItemCardMenuAction(action)}</React.Fragment>)}
    </div>
);

const ItemCardMenuAction = ({key, label, run}:ItemAction) => {
    const icon = ITEM_ACTIONS_ICONS_DICT[key];
    return (
        <a
            className="dropdown-item"
            xd-qa-type="dropdown_item"
            xd-qa-name={label}
            onClick={run}
        >
            <i className="icon">{icon && <FontAwesomeIcon icon={icon}/>}</i>
            {key === "xpressLinks"
                ? (<img src={require("./order-history-expansion/item-card/xpress-links.png")} alt="Xpress Links" xd-qa-type="icon" xd-qa-name="Xpresslinks Icon"/>)
                : label
            }
        </a>
    );
};

const ItemCardDetail = ({attribute, item}:{
    attribute   : ItemAttribute,
    item        : Item,
}) => {
    if (attribute.key === "trackDeliveryStatus") {
        return (
            <li>
                <span className="label">{attribute.label}</span>
                <span xd-qa-type="label" xd-qa-name={attribute.label}>{item.deliveryStatus}</span>
            </li>
        );
    } else if ("value" in attribute) {
        return (
            <li>
                <span className="label">{attribute.label}</span>
                <span xd-qa-type="label" xd-qa-name={attribute.label}>{attribute.key === "price" ? formatMoney(attribute.value) : attribute.value}</span>
            </li>
        )
    } else {
        throw new Error(`Missing renderer for item attribute '${attribute.key}'`);
    }
};